// GlobalDialog.js
import React, { useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import {
  Button,
  DialogActions,
  DialogContent,
  DialogContentText,
  Typography,
} from "@mui/material";
import { clearDialog } from "../../features/dialog/dialogSlice";
import SuccessDialog from "./SuccessDialog";
import { apiSlice } from "../../app/api/apiSlice";
import ErrorDialog from "./ErrorDialog";
import { useNavigate } from "react-router-dom";
import QRDialog from "./QRDialog";
import CreateSurveyDialog from "../../sections/@dashboard/survey/CreateSurveyDialog";
import MemberActionsDialog from "./MemberActionsDialog";
import AddNewMemberDialog from "../../sections/@dashboard/members/AddNewMemberDialog";

import ChangeDegreeDialog from "./ChangeDegreeDialog";
import UserDialog from "../../sections/@dashboard/user/UserDialog";
import DeleteUserDialog from "src/sections/@dashboard/user/DeleteUserDialog";
import PasswordChangeDialog from "src/sections/@dashboard/user/PasswordChangeDialog";
import SitesForm from "../../sections/@dashboard/sites/SitesForm";
import DeleteSiteDialogContent from "./DeleteSiteDialogContent";
import DeleteMemberDialogContent from "./DeleteMemberDialogContent";
import { Icon } from "@iconify/react";
import RedirectDialog from "./RedirectDialog";
import WelcomeDialog from "./WelcomeDialog";
import MemberWelcomeDialog from "./MemberWelcomeDialog";
import EditStatusDialog from "./EditStatusDialog";
import TestingEventDialog from "../events/TestingEventDialog";
import EventSelectDialog from "../events/EventSelectDialog";
import BulkEditMembersDialog from "./BulkEditMembersDialog";
import SiteOverviewDialog from "./SiteOverviewDialog";
import DataImportWizard from "../import/DataImportWizard";
import MemberRegistrationLinksDialog from "./MemberRegistrationLinksDialog";

const GlobalDialog = ({ dialog }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [close, setClose] = useState(false);

  const handleAskClose = (answer) => {
    if (["EDIT", "DELETE"].includes(dialog?.type)) {
      // Close dialog immediately if User edits or delets
      handleClose("REFETCH");
      return null;
    }

    if (answer === false) {
      // Ask user if he really wants to close if he is typing in new data
      setClose(answer);
      return null;
    }
    setClose(true);
  };

  const handleClose = (refetch) => {
    if (refetch === "REFETCH") {
      console.log("dialog :>> ", dialog);
      // Refetch all after success dialog
      dispatch(apiSlice.util.resetApiState());
      // refetchOffices();
    }
    dispatch(clearDialog());

    if (dialog?.redirect) {
      return navigate(dialog?.redirect ?? "/");
    }

    if (dialog?.reload) {
      window.location.reload();
    }
  };

  const renderContent = () => {
    switch (dialog.title) {
      case "Neuen Standort erstellen":
        return <SitesForm dialogType={dialog.type} />;
      case "Standort bearbeiten":
        return <SitesForm dialogType={dialog.type} siteData={dialog.data} />;
      case "Standort löschen":
        return (
          <DeleteSiteDialogContent
            dialogType={dialog.type}
            siteData={dialog.data}
          />
        );
      case "Mitglieder löschen":
        return (
          <DeleteMemberDialogContent
            dialogType={dialog.type}
            member={dialog?.data ?? null}
          />
        );
      case "Alert":
        return <h1>This is a alert</h1>;
      default:
        return null;
    }
  };

  if (close) {
    return (
      <div>
        <Dialog
          open
          keepMounted
          aria-describedby="alert-dialog-slide-description"
        >
          <DialogTitle>{dialog?.title}</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-slide-description">
              {dialog?.form}
            </DialogContentText>
            <Typography>Wollen Sie wirklich beenden?</Typography>
            <Typography variant="body1">
              <b>Alle Daten gehen verloren.</b>
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => handleAskClose(false)}>Zurück</Button>
            <Button variant="contained" color="error" onClick={handleClose}>
              Ja, beenden
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  }

  if (dialog.content === "delete-user") {
    return <DeleteUserDialog handleClose={handleClose} dialog={dialog} />;
  }

  if (dialog?.type === "password-change") {
    return (
      <PasswordChangeDialog handleClose={handleClose} user={dialog?.user} />
    );
  }

  if (dialog?.type === "BULK_ADD_TO_TESTING") {
    return (
      <TestingEventDialog
        open
        onClose={() => handleClose()}
        selectedMembers={dialog?.data?.members || dialog?.data || []}
        defaultTitle={dialog?.title || "Zur Prüfungsliste hinzufügen"}
      />
    );
  }

  if (dialog?.type === "BULK_ADD_TO_EVENT") {
    return (
      <EventSelectDialog
        open
        onClose={() => handleClose()}
        selectedMembers={dialog?.data?.members || dialog?.data || []}
        defaultTitle={dialog?.title || "Zu Veranstaltung hinzufügen"}
      />
    );
  }

  if (dialog?.type === "BULK_EDIT") {
    return (
      <BulkEditMembersDialog
        open
        handleClose={() => handleClose()}
        dialog={dialog}
      />
    );
  }

  if (dialog?.type === "SEND_REGISTRATION_LINKS") {
    return <MemberRegistrationLinksDialog handleClose={handleClose} />;
  }

  if (dialog.type === "REDIRECT") {
    return <RedirectDialog dialog={dialog} handleClose={handleClose} />;
  }

  if (dialog.content === "create-user-form") {
    return <UserDialog onClose={handleClose} dialog={dialog} />;
  }

  if (dialog.content === "edit-status") {
    return <EditStatusDialog dialog={dialog} handleClose={handleClose} />;
  }

  if (dialog.content === "QR") {
    return <QRDialog dialog={dialog} handleClose={handleClose} />;
  }

  if (dialog.content === "member-welcome") {
    return <MemberWelcomeDialog handleClose={handleClose} />;
  }

  if (dialog.content === "welcome") {
    return <WelcomeDialog userData={dialog?.data} handleClose={handleClose} />;
  }

  if (dialog.content === "data-import") {
    return <DataImportWizard handleClose={handleClose} />;
  }

  if (dialog.content === "SURVEY") {
    return <CreateSurveyDialog dialog={dialog} handleClose={handleClose} />;
  }

  if (dialog.content === "site-overview") {
    return <SiteOverviewDialog dialog={dialog} handleClose={handleClose} />;
  }

  if (dialog?.type === "ERROR") {
    return <ErrorDialog dialog={dialog} handleClose={handleClose} />;
  }

  if (dialog?.type === "SUCCESS") {
    return <SuccessDialog dialog={dialog} handleClose={handleClose} />;
  }

  if (dialog?.type === "MEMBERACTIONS") {
    return <MemberActionsDialog handleClose={handleClose} dialog={dialog} />;
  }

  if (dialog?.title === "Neues Mitglied erstellen") {
    return (
      <div>
        <AddNewMemberDialog
          handleClose={() => handleClose()}
          handleAskClose={() => handleAskClose()}
        />
      </div>
    );
  }

  if (dialog?.title === "Gürtelgrad ändern") {
    return (
      <div>
        <ChangeDegreeDialog
          handleClose={() => handleClose()}
          member={dialog?.data}
        />
      </div>
    );
  }

  return (
    <Dialog open maxWidth="lg">
      <DialogTitle>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {dialog?.title ?? "Dialog"}
          <Button onClick={handleAskClose} color="inherit">
            <Icon width="40" icon="icon-park-solid:close-one" />
          </Button>
        </div>
      </DialogTitle>
      <DialogContent>
        <Typography variant="h3">{dialog?.content?.header ?? ""}</Typography>
        <Typography variant="h6">{dialog?.content?.text ?? ""}</Typography>
        {dialog?.form && (
          <DialogContentText id="alert-dialog-slide-description">
            {dialog?.form}
          </DialogContentText>
        )}
        {dialog && renderContent()}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleAskClose}
          color="error"
          variant="contained"
          autoFocus
        >
          SCHLIESSEN
        </Button>
      </DialogActions>
    </Dialog>
  );
};

GlobalDialog.propTypes = {
  dialog: PropTypes.object.isRequired,
};

export default GlobalDialog;
